/**
 * Stripe Checkout Form Component
 * 
 * Handles embedded Stripe payment form for subscriptions.
 */

import React, { useState, useEffect } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Button, Modal, message, Typography, Spin } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const StripeCheckoutForm = ({
  clientSecret,
  onSuccess,
  onError,
  user,
  planName,
  addMerchantCurrentPlan,
  signoutUser,
  duration = 1,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [isSucceeded, setIsSucceeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Check for redirect result
  useEffect(() => {
    if (!stripe) return;

    const clientSecretFromUrl = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecretFromUrl) return;

    stripe.retrievePaymentIntent(clientSecretFromUrl).then(({ paymentIntent }) => {
      if (paymentIntent.status === "succeeded") {
        setIsSucceeded(true);
        handlePaymentSuccess();
      } else {
        setErrorMessage("Payment was not successful. Please try again.");
        onError && onError(new Error("Payment failed"));
      }
    });
  }, [stripe]);

  const handlePaymentSuccess = async () => {
    try {
      // Calculate expiration date
      const currentDate = new Date();
      const expiresOn = new Date(currentDate);
      expiresOn.setDate(expiresOn.getDate() + 28 * duration);

      const transRef = `stripe_sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Update merchant plan
      await addMerchantCurrentPlan(user.email, {
        plan: planName,
        currency_code: user.currency || user.employer?.currency,
        no_of_days: 28 * duration,
        expires_on: expiresOn.toLocaleDateString(),
        transaction_reference: transRef,
      });

      message.success("Payment successful! Subscription activated.");

      // Notify parent
      onSuccess && onSuccess();

      // Sign out to refresh session
      Modal.success({
        title: "Subscription Activated!",
        content: "You will be logged out. Please log back in to continue enjoying the service.",
        onOk: () => {
          signoutUser();
        },
      });
    } catch (err) {
      console.error("Error updating merchant plan:", err);
      message.error("Payment successful, but there was an error updating your account. Please contact support.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/d/account?subscription=success`,
        },
        redirect: "if_required",
      });

      if (error) {
        if (error.type === "card_error" || error.type === "validation_error") {
          setErrorMessage(error.message);
          message.error(error.message);
        } else {
          setErrorMessage("An unexpected error occurred.");
          message.error("An unexpected error occurred.");
        }
        onError && onError(error);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        setIsSucceeded(true);
        await handlePaymentSuccess();
      }
    } catch (err) {
      setErrorMessage(err.message || "Payment failed");
      message.error(err.message || "Payment failed");
      onError && onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSucceeded) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a" }} />
        <Title level={3} style={{ marginTop: 16 }}>
          Payment Successful!
        </Title>
        <Text>Your subscription has been activated.</Text>
      </div>
    );
  }

  return (
    <div className="stripe-checkout-form">
      <form onSubmit={handleSubmit}>
        <Title level={5}>Complete Your Payment</Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
          Enter your card details below to complete your subscription.
        </Text>

        <div style={{ marginBottom: 24 }}>
          <PaymentElement
            options={{
              layout: "tabs",
            }}
          />
        </div>

        {errorMessage && (
          <div style={{ color: "#ff4d4f", marginBottom: 16 }}>
            {errorMessage}
          </div>
        )}

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={isLoading}
          disabled={!stripe || !elements || isLoading}
          style={{ height: 48, fontSize: 16 }}
        >
          {isLoading ? <Spin /> : "Pay Now"}
        </Button>

        <Text
          type="secondary"
          style={{ display: "block", textAlign: "center", marginTop: 16 }}
        >
          Your payment is secured by Stripe. We never store your card details.
        </Text>
      </form>
    </div>
  );
};

export default StripeCheckoutForm;
